import { IEmployeeApiPort } from 'domains/personnel/application/ports/IEmployeeApiPort';
import type { EmployeePinValidateDto } from 'domains/personnel/application/dtos/EmployeeRequestDto';
import { EmployeeHttpClientType } from '@personnel/infrastructure/api/types/index';
import { EmployeeApiMapperType } from '@personnel/infrastructure/mappers/types';
import {
  EmployeeRequestDto,
  EmployeeDetailRequestDto,
  EmployeeUpdateDto,
  EmployeeCreateDto,
  EmployeeDeleteDto,
  EmployeeBulkDeleteDto,
} from 'domains/personnel/application/dtos';
import {
  EmployeeDto,
  PinGenerateResponseDto,
  PinValidateResponseDto,
  EmployeeDeleteResponseDto,
  EmployeeBulkDeleteResponseDto,
} from 'domains/personnel/application/dtos/EmployeeResponseDto';

/**
 * Creates the Employee API repository which orchestrates HTTP client and mappers
 * to execute application-level operations using API contracts.
 *
 * @example
 * const repository = createEmployeeApiRepository({ httpClient, mapper });
 * const response = await repository.fetchUsers({ page: 0, limit: 10 });
 */
export const createEmployeeApiRepository = (props: {
  httpClient: EmployeeHttpClientType;
  mapper: EmployeeApiMapperType;
}): IEmployeeApiPort => {
  const { httpClient, mapper } = props;

  /**
   * Fetches users from the API using application DTO.
   * Transforms `EmployeeRequestDto` to API request via mapper and returns the API response DTO.
   *
   * @param {EmployeeRequestDto} request - Application request DTO for employees list
   * @returns {Promise<import('domains/personnel/application/dtos').EmployeeApiResponseDto>} Employees response DTO
   *
   * @example
   * const response = await fetchUsers({ search: 'John', limit: 20 });
   */
  const getEmployees = async (request: EmployeeRequestDto) => {
    const apiRequest = mapper.mapEmployeeRequestDtoToApiRequest(request);
    const apiResponse = await httpClient.fetchUsers(apiRequest);
    const employeeResponse = mapper.mapEmployeeResponseToDto(apiResponse);
    return employeeResponse;
  };

  const getEmployeeByUuid = async (request: EmployeeDetailRequestDto) => {
    const apiResponse = await httpClient.getUser({
      userUuid: request.userUuid,
      includeAssignedStores: request.includeAssignedStores,
    });
    return mapper.mapEmployeeDetailResponseToDto(apiResponse.data);
  };

  /**
   * Creates a new employee through the API
   * Uses existing createUserV2 from httpClient
   *
   * @param {EmployeeCreateDto} request - Employee creation data
   * @returns {Promise<EmployeeDto>} Created employee DTO
   */
  const createEmployee = async (request: EmployeeCreateDto): Promise<EmployeeDto> => {
    const apiRequest = mapper.mapEmployeeCreateDtoToApiRequest(request);
    const apiResponse = await httpClient.createUserV2(apiRequest);
    return mapper.mapEmployeeCreateResponseToDto(apiResponse);
  };

  const updateEmployee = async (request: EmployeeUpdateDto): Promise<EmployeeDto> => {
    const apiRequest = mapper.mapEmployeeUpdateDtoToApiRequest(request);
    const apiResponse = await httpClient.updateUsers(apiRequest);
    return mapper.mapEmployeeUpdateResponseToDto(apiResponse);
  };

  /**
   * Deletes an employee through the API
   *
   * @param {EmployeeDeleteDto} request - Employee deletion data
   * @returns {Promise<EmployeeDeleteResponseDto>} Deletion result with success status
   */
  const deleteEmployee = async (request: EmployeeDeleteDto): Promise<EmployeeDeleteResponseDto> => {
    await httpClient.deleteUserV2({ userUuid: request.userUuid });
    return {
      success: true,
      deletedUuid: request.userUuid,
    };
  };

  /**
   * Generates a new PIN for an employee
   *
   * @returns {Promise<PinGenerateResponseDto>} Generated PIN response
   */
  const generatePin = async (): Promise<PinGenerateResponseDto> => {
    const data = await httpClient.generatePin();
    return { pin: data.pin };
  };

  /**
   * Validates an employee PIN
   *
   * @param {EmployeePinValidateDto} request - PIN validation request
   * @returns {Promise<PinValidateResponseDto>} PIN validation response
   */
  const validatePin = async (request: EmployeePinValidateDto): Promise<PinValidateResponseDto> => {
    const payload: { pin: string; userUuid?: string } = { pin: request.pin };

    if (request.userUuid) {
      payload.userUuid = request.userUuid;
    }

    const data = await httpClient.validatePin(payload);
    return { valid: data.valid };
  };

  /**
   * Bulk deletes employees through the API
   *
   * @param {EmployeeBulkDeleteDto} request - Bulk deletion request with UUIDs
   * @returns {Promise<EmployeeBulkDeleteResponseDto>} Bulk deletion result
   */
  const bulkDeleteEmployees = async (request: EmployeeBulkDeleteDto): Promise<EmployeeBulkDeleteResponseDto> => {
    await httpClient.bulkDeleteUsers({ uuids: [...request.uuids] });
    return {
      success: true,
      deletedCount: request.uuids.length,
      deletedUuids: [...request.uuids],
    };
  };

  return {
    getEmployees,
    getEmployeeByUuid,
    createEmployee,
    updateEmployee,
    deleteEmployee,
    generatePin,
    validatePin,
    bulkDeleteEmployees,
  };
};
